import React, { useCallback, useMemo, useState } from "react";
import { Spin, notification } from "antd";
import { loadStripe } from "@stripe/stripe-js";
import FundsOverview from "./FundsOverview";
import PurchaseHistory from "./PurchaseHistory";
import BuyFundsModal from "./BuyFundsModal";
import RefundConfirmModal from "./RefundConfirmModal";
import { useFundsData } from "./useFundsData";
import { use3dsRedirectResult } from "./use3dsRedirectResult";
import { fundsService } from "./fundsService";
import { extractApiErrorMessage, openInvoice } from "./utils";
import { withErrorBoundary } from "../ErrorBoundary/withErrorBoundary";

import "./index.scss";

const { REACT_APP_STRIPE_PUBLISHABLEKEY } = process.env;

const Funds = () => {
   // loadStripe must be called once, not on every render, or Stripe.js is
   // reloaded and the <Elements> instance churns.
   const stripePromise = useMemo(() => loadStripe(REACT_APP_STRIPE_PUBLISHABLEKEY), []);

   const { isLoading, overview, history, savedCard, refresh } = useFundsData();
   const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
   const [isDownloadingAll, setIsDownloadingAll] = useState(false);
   // The transaction awaiting refund confirmation; null when the modal is closed.
   const [refundTarget, setRefundTarget] = useState(null);
   const [isRefunding, setIsRefunding] = useState(false);

   // Resolve a 3D Secure full-page redirect (some issuers redirect instead of
   // using the in-app modal), then refresh so the new balance shows up.
   use3dsRedirectResult({ stripePromise, onSuccess: refresh });

   const openBuyModal = useCallback(() => setIsBuyModalOpen(true), []);
   const closeBuyModal = useCallback(() => setIsBuyModalOpen(false), []);
   const handleDownloadItem = useCallback((record) => openInvoice(record?.invoiceUrl), []);
   // Server zips every invoice and streams it back as a blob; we turn that into
   // a browser download rather than opening each PDF in its own tab.
   const handleDownloadAll = useCallback(() => {
      setIsDownloadingAll(true);
      fundsService
         .downloadAllInvoices()
         .then((res) => {
            if (!res?.data || res.data.size === 0) {
               throw new Error("Received empty file.");
            }
            const blob = new Blob([res.data], { type: "application/zip" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = "Invoices.zip";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            notification.success({ message: "Invoices downloaded successfully!" });
         })
         .catch((error) => {
            console.error("Download error:", error);
            notification.error({
               message: "Error downloading invoices, please try again later."
            });
         })
         .finally(() => setIsDownloadingAll(false));
   }, []);
   const handleRefundItem = useCallback((record) => setRefundTarget(record), []);
   const closeRefundModal = useCallback(() => setRefundTarget(null), []);
   const confirmRefund = useCallback(() => {
      if (!refundTarget?.transactionId) return;
      setIsRefunding(true);
      fundsService
         .refund(refundTarget.transactionId)
         .then((res) => {
            // Prefer the server's wording ("Refund initiated. It will be
            // processed shortly.") so the toast matches what actually happened.
            notification.success({
               message: res?.message || "Refund initiated. It will be processed shortly."
            });
            refresh();
         })
         .catch((error) => {
            // A 200-with-isSuccess:false rejection is the bare envelope and is
            // already toasted by the global axios interceptor; notify only for
            // the HTTP error path (e.g. a 400 "already refunded"), which the
            // interceptor skips — otherwise the refund fails silently.
            if (error?.isSuccess === false && !error?.response) return;
            notification.error({
               message: extractApiErrorMessage(
                  error,
                  "Could not refund this transaction. Please try again later."
               )
            });
         })
         .finally(() => {
            setIsRefunding(false);
            setRefundTarget(null);
         });
   }, [refundTarget, refresh]);

   return (
      <div className="funds">
         <p className="title">Balance</p>

         {isLoading ? (
            <Spin className="funds__spinner" />
         ) : (
            <>
               <FundsOverview overview={overview} onBuyFunds={openBuyModal} />

               <PurchaseHistory
                  history={history}
                  isLoading={false}
                  isDownloadingAll={isDownloadingAll}
                  onDownloadItem={handleDownloadItem}
                  onDownloadAll={handleDownloadAll}
                  onRefundItem={handleRefundItem}
               />

               {isBuyModalOpen && (
                  <BuyFundsModal
                     isOpen={isBuyModalOpen}
                     onClose={closeBuyModal}
                     savedCard={savedCard}
                     stripePromise={stripePromise}
                     onPurchased={refresh}
                  />
               )}

               {refundTarget && (
                  <RefundConfirmModal
                     isOpen={Boolean(refundTarget)}
                     onClose={closeRefundModal}
                     onConfirm={confirmRefund}
                     isRefunding={isRefunding}
                  />
               )}
            </>
         )}
      </div>
   );
};

export default withErrorBoundary(Funds);
